import { useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { UploadCloud } from 'lucide-react'
import { Button, Chip, Drawer, PartnerPicker } from '@/components/ui'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { useCreateLetter, useUploadPriceWorkbook } from '../api/letterQueries'
import type { UploadResult, UploadedSheet } from '../api/letterTypes'

export interface NewPriceLetterDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function guess(sheet: UploadedSheet): PickerValue {
  const m = sheet.customer_match.match
  return m ? [m.id, m.name] : null
}

/**
 * "New letter" drawer: upload the internal price-review workbook, then pick
 * the customer for each sheet (prefilled from the sheet name when it matches
 * one company) and create that sheet's draft letter. One customer per sheet.
 */
export function NewPriceLetterDrawer({ open, onOpenChange }: NewPriceLetterDrawerProps) {
  const navigate = useNavigate()
  const fileRef = useRef<HTMLInputElement | null>(null)
  const upload = useUploadPriceWorkbook()
  const create = useCreateLetter()
  const [result, setResult] = useState<UploadResult | null>(null)
  const [picked, setPicked] = useState<Record<number, PickerValue>>({})

  function onFile(file: File | undefined) {
    if (!file) return
    upload.mutate(file, {
      onSuccess: (res) => {
        setResult(res)
        setPicked(Object.fromEntries(res.sheets.map((s) => [s.index, guess(s)])))
      },
    })
  }

  function createFor(sheet: UploadedSheet) {
    const partner = picked[sheet.index]
    if (!result || !partner) return
    create.mutate(
      { document_id: result.document_id, sheet_index: sheet.index, partner_id: partner[0] },
      {
        onSuccess: (detail) => {
          onOpenChange(false)
          void navigate({
            to: '/pricelists/letters/$letterId',
            params: { letterId: detail.letter.id },
          } as never)
        },
      },
    )
  }

  function close() {
    setResult(null)
    setPicked({})
    upload.reset()
    create.reset()
    onOpenChange(false)
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      size="wide"
      title="New price change letter"
      meta="Upload the internal price review workbook (.xlsx). Each sheet is one customer. Cost, margin, forecast and notes columns are never put on the letter."
      footer={
        <div className="flex w-full items-center justify-end">
          <Button variant="secondary" size="sm" onClick={close}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {!result ? (
          <div
            onDrop={(e) => {
              e.preventDefault()
              onFile(e.dataTransfer?.files?.[0])
            }}
            onDragOver={(e) => e.preventDefault()}
            className="flex flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed border-border-default bg-surface-sunk px-6 py-10 text-center"
          >
            <UploadCloud size={28} className="text-tertiary" />
            <p className="m-0 text-sm text-secondary">Drag &amp; drop the workbook here</p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.XLSX,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              data-testid="letter-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0]
                e.target.value = ''
                onFile(f)
              }}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
            >
              {upload.isPending ? 'Reading…' : 'Choose file'}
            </Button>
            <p className="m-0 text-xs text-tertiary">XLSX only, up to 25 MB</p>
            {upload.isError ? (
              <p className="m-0 text-xs text-danger">{(upload.error as Error).message}</p>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <span className="text-role-section-label">{result.filename}</span>
            {result.sheets.map((sheet) => (
              <div
                key={sheet.index}
                className="flex flex-col gap-2 rounded-sm border border-border-default bg-surface p-3"
              >
                <div className="flex items-center gap-2">
                  <span className="text-role-data-primary">{sheet.name}</span>
                  <Chip size="xs">{sheet.row_count} rows</Chip>
                  {sheet.discontinued_count > 0 ? (
                    <Chip size="xs" variant="warning">
                      {sheet.discontinued_count} no longer supplied
                    </Chip>
                  ) : null}
                </div>
                {sheet.warnings.map((w) => (
                  <p key={w} className="m-0 text-xs text-warning-text">
                    {w}
                  </p>
                ))}
                {sheet.customer_match.candidates?.length ? (
                  <p className="m-0 text-xs text-tertiary">
                    Several companies match this sheet name. Pick the right one.
                  </p>
                ) : null}
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <PartnerPicker
                      kind="customer"
                      variant="field"
                      placeholder="Customer for this sheet…"
                      value={picked[sheet.index] ?? null}
                      onChange={(v) => setPicked((p) => ({ ...p, [sheet.index]: v }))}
                    />
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!picked[sheet.index] || sheet.row_count === 0 || create.isPending}
                    onClick={() => createFor(sheet)}
                  >
                    {create.isPending ? 'Creating…' : 'Create letter'}
                  </Button>
                </div>
              </div>
            ))}
            {create.isError ? (
              <p className="m-0 text-xs text-danger">{(create.error as Error).message}</p>
            ) : null}
          </div>
        )}
      </div>
    </Drawer>
  )
}
